import dayjs from "dayjs"
import * as React from "react"
import { Row } from "react-grid-system"
import styled, { keyframes } from "styled-components"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import { useAddBookmarkReceipt } from "../../hooks/useAddBookmarkReceipt"
import H3 from "../atoms/typography/H3"
import H2 from "../atoms/typography/H2"
import SmallButton from "../atoms/buttons/SmallButton"
import { BookmarkActiveIcon, BookmarkIcon } from "../atoms/icons/BookmarkIcons"
import { DownloadIcon } from "../atoms/icons/DownloadIcon"
import { useRemoveBookmarkReceipt } from "../../hooks/useRemoveBookmarkReceipt"
import { useTranslation } from "react-i18next"
import { useOrderPdfUrl } from "../../hooks/useOrderPdfUrl"
import { ArrowRight } from "../atoms/icons/ArrowRight"
import { AnimatePresence } from "framer-motion"
import Modal from "../molecules/Modals/Modal"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"
import { Order } from "../../utils/models"

export interface ReceiptProps {
  id: number
  previewSrc: string
  title: string
  date: any
  bookmarked?: boolean
  receiptUrl: string
  order?: Order
  onClickSave?: () => void
  buyer?: string
  readableOrderType: string
}

const CardFooterContainer = styled.div`
  background-color: ${(props) => props.theme.colors.greyLight};
  border-top: ${(props) => `${props.theme.borders.smallWidth} solid ${props.theme.colors.greyDark}`};
  padding: ${(props) => `${props.theme.spacings.large} ${props.theme.spacings.extreme}`};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
`

const PreviewImage = styled.img`
  margin: ${(props) => props.theme.spacings.extreme};
  object-fit: cover;
  width: 200px;
  max-height: 200px;
`

const IconButtons = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  :hover {
    filter: brightness(40%);
  }
`

const OverlayContainer = styled.div`
  background: rgba(38, 38, 38, 0.6);
  backdrop-filter: blur(1.5px);
  width: 100%;
  padding: ${(props) => props.theme.spacings.extreme};
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  height: calc(100% - 71px);
  visibility: hidden;
`

const fadeIn = keyframes`
  0% {opacity: 0}
  100%{opacity : 1}
`

const CardContainer = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  border: ${(props) => `${props.theme.borders.smallWidth} solid ${props.theme.colors.greyDark}`};
  box-shadow: 0px 1px 3px 1px rgba(119, 119, 119, 0.25);
  border-radius: ${(props) => props.theme.borders.softRadius};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: 540px;

  :hover {
    ${OverlayContainer} {
      animation: 0.4s ${fadeIn} ease-in;
      visibility: visible;
    }
  }
`

const ReceiptCard: React.FunctionComponent<ReceiptProps> = ({
  id,
  previewSrc,
  title,
  date,
  bookmarked,
  receiptUrl,
  order,
  onClickSave,
  buyer,
  readableOrderType,
}) => {
  const { t } = useTranslation()

  const numericId = parseInt(id.toString())
  const { getOrderPdfUrl, pdfUrl } = useOrderPdfUrl()
  const [pdfDownloaded, setPdfDownloaded] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined)

  const handleDownloadPdfClick = async (event: React.MouseEvent) => {
    event.preventDefault()
    if (!pdfUrl && order?.id) {
      getOrderPdfUrl({
        variables: {
          orderId: order.id,
        },
      })
        .then((response: any) => {
          if (response.error) {
            setErrorMessage(response.error.message)
          } else {
            setPdfDownloaded(true)
          }
        })
        .catch((error) => {
          captureExceptionWithContext(error, "error getting order pdf url")
        })
    } else {
      setPdfDownloaded(true)
    }
  }

  React.useEffect(() => {
    if (pdfDownloaded) {
      let downloadUrl = pdfUrl ? pdfUrl : receiptUrl
      window.open(downloadUrl, "_blank")
      setPdfDownloaded(false)
    }
  }, [pdfDownloaded])

  const onErrorAddBookmark = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error adding bookmark")
  }
  const { addBookmarkReceipt } = useAddBookmarkReceipt({
    onCompleted: () => {},
    onError: onErrorAddBookmark,
  })
  const onAddBookmark = async () => {
    try {
      await addBookmarkReceipt({
        variables: {
          id: numericId,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error adding bookmark")
    }
  }

  const onErrorRemoveBookmark = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error removing bookmark")
  }
  const { removeBookmarkReceipt } = useRemoveBookmarkReceipt({
    onCompleted: () => {},
    onError: onErrorRemoveBookmark,
  })
  const onRemoveBookmark = async () => {
    try {
      await removeBookmarkReceipt({
        variables: {
          id: numericId,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error removing bookmark")
    }
  }

  return (
    <div>
      <CardContainer>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <PreviewImage src={previewSrc} alt="Receipt" />
        </div>
        <OverlayContainer>
          <div>
            <H3 color={theme.colors.white} textAlign="center">
              {t("buyer")}: {buyer}
            </H3>
            <HorizontalSpacer spacing={3} />
            <Row justify="center">
              <a href={order ? pdfUrl : receiptUrl} target="__blank" onClick={handleDownloadPdfClick}>
                <SmallButton title="Open" icon={<ArrowRight />} />
              </a>
            </Row>
          </div>
        </OverlayContainer>
        <CardFooterContainer>
          <div data-testid={`receipt-card-summary-${order ? order.number : id}`}>
            <H3>{readableOrderType}</H3>
            <H3>{title}</H3>
            <H3>{dayjs(date).format("DD MMMM YYYY")}</H3>
          </div>
          <div style={{ display: "flex" }}>
            <IconButtons onClick={bookmarked ? onRemoveBookmark : onAddBookmark}>
              {bookmarked ? <BookmarkActiveIcon /> : <BookmarkIcon />}
            </IconButtons>
            <Spacer spacing={1} />

            <a
              data-testid={`download-receipt-${order ? order.number : id}`}
              href={order ? pdfUrl : receiptUrl}
              target="_blank"
              onClick={handleDownloadPdfClick}
            >
              <IconButtons>
                <DownloadIcon />
              </IconButtons>
            </a>
          </div>
        </CardFooterContainer>
      </CardContainer>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {errorMessage !== undefined && (
          <Modal open={errorMessage !== undefined} onClose={() => setErrorMessage(undefined)} closeIcon>
            <H2>Error</H2>
            <H3>{errorMessage}</H3>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ReceiptCard
